import { registerLocaleData } from '@angular/common';
import localeEsMx from '@angular/common/locales/es-MX';
import { ApplicationConfig, isDevMode, provideBrowserGlobalErrorListeners } from '@angular/core';
import { provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTranslateService } from '@ngx-translate/core';
import {
  AuthInterceptor,
  CORE_NAMESPACES,
  ErrorInterceptor,
  provideAuthCatalog,
  provideLanguagePreload,
  provideTrueformBrand,
  provideTrueformRouteTargets,
  provideTrueformUi,
} from '@trueform/ui/core';
import { provideNavigation } from '@trueform/ui/layout';
import { provideTrueformUas } from '@trueform/ui/uas';

import { provideDefaultClient as providePhsClient } from '@/api/phs-api/providers';
import { provideDcsClient } from '@/api/dcs-api/providers';
import { providePcsClient } from '@/api/pcs-api/providers';
import { LibraryE2eMockDelegatorInterceptor } from '@/core/e2e-mock-delegator.interceptor';
import { APP_TRUEFORM_BRAND } from '@/environments/brand.config';
import { environment } from '@/environments/environment';
import { APP_RESOURCE_ACTIONS } from '@/core/config/auth-permissions.config';
import { appErrorToastFilter } from '@/core/config/error-toast-policy';
import { DEFAULT_APP_NAV_SECTIONS } from '@/core/config/navigation.config';
import { routes } from './app.routes';

// WUIAM-124 / WUIAM-58: register the Spanish locale data ONCE, before
// bootstrap. Angular's `formatDate` throws `NG0701` for an `es`-family tag
// with no registered data, so the shared date pipe (`shared/date`) depends on
// this. The app registers the es-MX data EXPLICITLY — the exact data
// `@trueform/ui` registers from its own `registerCivilDateLocales()` when a
// civil date field renders — so the output is deterministic (`1 sep 2026`,
// `3:30:46 p.m.`) regardless of whether a date field rendered first. Plain
// `es` is NOT registered: every caller resolves through `appLocaleFor()` to
// `es-MX`/`en-US`, so an `es` tag is never requested. `LOCALE_ID` is
// deliberately NOT provided — the pipe is impure and selects the locale
// reactively (ARCH-036 §3).
registerLocaleData(localeEsMx);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // The library's runtime contract (Wave 1 §1): config, brand, and the
    // app's own navigation/route data. Nothing below may import `environment`
    // from shared code — the app hands it in here.
    //
    // `errorToastFilter` (REQ-025) is the app's ONE veto over the library's
    // automatic error toast: DCS, PCS and PHS failures are rendered inside the
    // app's own sheets/forms, so they stay silent (the error is still
    // rethrown, and 401 keeps the library's logout/redirect flow). See
    // `core/config/error-toast-policy.ts`.
    provideTrueformUi({ ...environment, errorToastFilter: appErrorToastFilter }),
    provideTrueformBrand(APP_TRUEFORM_BRAND),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideTranslateService(),
    // App-owned i18n namespaces on top of the library's core list: `brand`
    // (the one `public/assets/i18n/brand/*.json` file the library never ships),
    // `shared` (app-shared UI copy with no feature owner — e.g. the
    // `shared.lookupFilter.*` labels of `shared/ui/lookup-filter`, WUIAM-115),
    // `prices` (Price Hub), `catalogs` (DCS catalog screens), `priceCalculator`
    // (PCS reference-data screens), `fxRates` (PHS FX rates screen) and
    // `pcsCalculator` (the Lite Calculator — DESIGN-004 §6, TASK-188; its
    // `lite.*` keys live in this separate namespace, D-16). NOTE:
    // `provideLanguagePreload` provides the token with a plain `useValue`, so
    // repeated calls do NOT accumulate — the LAST call wins. Keep this a SINGLE
    // call listing every app namespace (the pre-existing two-call form silently
    // dropped all but the last; consolidated here by TASK-101).
    provideLanguagePreload([
      ...CORE_NAMESPACES,
      'brand',
      'shared',
      'prices',
      'catalogs',
      'priceCalculator',
      'fxRates',
      'pcsCalculator',
    ]),
    provideNavigation(DEFAULT_APP_NAV_SECTIONS),
    // The generated DCS client (TASK-001); DCS list responses stay camelCase
    // and are mapped by the screen-kit fetchers (gotchas §5).
    provideDcsClient({ basePath: environment.dcsApiUrl }),
    // The generated PCS client (TASK-099): pricing calculations, freight/DDU
    // tariffs, and import taxes. PCS wire dates are plain strings end to end
    // (civil `YYYY-MM-DD`; the lone `executed_at` date-time has no app
    // consumer), so the client is generated with `dateType: 'string'`, the
    // models type every date as `string`, and the generator emits NO date
    // interceptor — which is also why the PDF/XLSX `Blob` export body is never
    // rebuilt into `{}` (a Blob has no own enumerable keys) and the download in
    // `exporting` keeps working (WUIAM-80).
    providePcsClient({ basePath: environment.pcsApiUrl }),
    // The RBAC extension point: this app's own catalogue (declared in
    // `core/config/auth-permissions.config.ts`, alongside the
    // `TrueformResourceActions` type augmentation) merges OVER the library
    // baseline at runtime. It ships with zero app resources; adding
    // `ng.orders` is an edit to that ONE file. Walkthrough: docs/permissions.md.
    provideAuthCatalog(APP_RESOURCE_ACTIONS),
    // The library screens navigate through these destinations instead of
    // assuming this app's URLs. Keep them in sync with `app.routes.ts` (the
    // route records) and `core/config/public-routes.ts` (the chrome policy) —
    // together they are where this app declares its routes once.
    provideTrueformRouteTargets({
      login: ['/login'],
      landing: ['/dashboard'],
      forgotPassword: ['/forgot-password'],
      resetPassword: ['/reset-password'],
      unauthorizedFallback: ['/dashboard'],
    }),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    // The library's UAS contract client plus the default users/roles port
    // adapters (WUIAM-140): one library-owned mapping of the UAS wire
    // (`page`/`page_size`, `page_size` envelope, `expected_version` bodies)
    // instead of an app hand-rolled transport. The base URL comes from
    // `apiUrl` in the config above, which IS this app's UAS host.
    provideTrueformUas(),
    // The generated PHS client owns its own `BASE_PATH_DEFAULT` token instance,
    // so pointing it at `environment.phsApiUrl` here is what makes every
    // `PricesService` / `SourcesService` call hit the real host. Without
    // it the client falls back to `/api` and every request 404s. The import is
    // aliased because auth/pos/phs all export the same `provideDefaultClient`.
    providePhsClient({ basePath: environment.phsApiUrl }),
    // T1 ([[TASK-049]]): a thin eager delegator keeps the library e2e-only mock
    // at its exact chain position while its heavy code is lazily imported only
    // under `?e2e=true`, so it stays out of the initial chunk. The app-owned
    // DCS/UAS mock is now MSW (`src/testing/msw/**`, registered from `main.ts`'s
    // e2e bootstrap) — [[TASK-071]] deleted its delegator + provider.
    { provide: HTTP_INTERCEPTORS, useClass: LibraryE2eMockDelegatorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  ],
};
